import { Button } from "@/components/ui/UIElements/Button";
import React from "react";
import { useToast } from "@finitless/shared/hooks";
import {
  handleApiError,
  handleApiSuccess,
  useGlobalUtils,
} from "@finitless/shared/lib";
import { Switch, Icon } from "../";
import { Pagination } from "../Data";
import { DeleteConfirmationModal } from "../Modals";
import {
  useExtraOptions,
  useToggleExtraOption,
  useToggleExtraSuboption,
  useDeleteExtraOption,
  useDeleteExtraSuboption,
} from "../../../hooks/useProducts";
import CreateOptionModal from "./CreateOptionModal";
import CreateSuboptionModal from "./CreateSuboptionModal";

const ExtraOptions = ({ businessId, extraId, hasPosIntegration }) => {
  const toast = useToast();
  const { parsePrice } = useGlobalUtils();
  const [page, setPage] = React.useState(1);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [optionToDelete, setOptionToDelete] = React.useState(null);
  const [optionToEdit, setOptionToEdit] = React.useState(null);
  const [isCreateSuboptionOpen, setIsCreateSuboptionOpen] =
    React.useState(false);
  const [isDeleteSuboptionOpen, setIsDeleteSuboptionOpen] =
    React.useState(false);
  const [currentOptionId, setCurrentOptionId] = React.useState(null);
  const [suboptionToEdit, setSuboptionToEdit] = React.useState(null);
  const [suboptionToDelete, setSuboptionToDelete] = React.useState(null);
  const [updatingSuboptionId, setUpdatingSuboptionId] = React.useState(null);

  const { data, isLoading } = useExtraOptions({
    businessId,
    extraId,
    page,
    pageSize: 10,
  });
  const options = data?.options || [];
  const pagination = data?.pagination || {};

  const toggleOptionMutation = useToggleExtraOption();
  const toggleSuboptionMutation = useToggleExtraSuboption();
  const deleteOptionMutation = useDeleteExtraOption();
  const deleteSuboptionMutation = useDeleteExtraSuboption();

  const handleToggleOption = async (option, value) => {
    try {
      await toggleOptionMutation.mutateAsync({
        businessId,
        extraId,
        option: {
          id: option.id,
          name: option.name,
          min: option.min,
          max: option.max,
          enabled: value,
        },
      });
      handleApiSuccess("Option updated successfully", toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Update Option",
        defaultMessage: "Failed to update option. Please try again.",
      });
    }
  };

  const handleToggleSuboption = async (optionId, suboption, value) => {
    setUpdatingSuboptionId(suboption.id);
    try {
      await toggleSuboptionMutation.mutateAsync({
        businessId,
        extraId,
        optionId,
        suboption: { id: suboption.id, enabled: value },
      });
      handleApiSuccess("Suboption updated successfully", toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Update Suboption",
        defaultMessage: "Failed to update suboption. Please try again.",
      });
    } finally {
      setUpdatingSuboptionId(null);
    }
  };

  const handleDeleteOption = (option) => {
    setOptionToDelete(option);
    setIsDeleteOpen(true);
  };

  const confirmDeleteOption = async () => {
    if (!optionToDelete) return;
    try {
      await deleteOptionMutation.mutateAsync({
        businessId,
        extraId,
        optionId: optionToDelete.id,
      });
      handleApiSuccess("Option deleted successfully", toast);
      setIsDeleteOpen(false);
      setOptionToDelete(null);
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Delete Option",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleDeleteSuboption = (optionId, suboption) => {
    setCurrentOptionId(optionId);
    setSuboptionToDelete(suboption);
    setIsDeleteSuboptionOpen(true);
  };

  const confirmDeleteSuboption = async () => {
    if (!suboptionToDelete || !currentOptionId) return;
    try {
      await deleteSuboptionMutation.mutateAsync({
        businessId,
        extraId,
        optionId: currentOptionId,
        suboptionId: suboptionToDelete.id,
      });
      handleApiSuccess("Suboption deleted successfully", toast);
      setIsDeleteSuboptionOpen(false);
      setSuboptionToDelete(null);
      setCurrentOptionId(null);
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Delete Suboption",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleCreateSuboption = (optionId) => {
    setCurrentOptionId(optionId);
    setSuboptionToEdit(null);
    setIsCreateSuboptionOpen(true);
  };

  const handleEditSuboption = (optionId, suboption) => {
    setCurrentOptionId(optionId);
    setSuboptionToEdit(suboption);
    setIsCreateSuboptionOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="border-primary h-6 w-6 animate-spin rounded-full border-b-2" />
      </div>
    );
  }
  return (
    <div
      className="ordering-catalog-surface space-y-3 p-2"
      data-ordering-expression="product"
    >
      <div className="flex items-center justify-between">
        <div className="text-foreground text-sm">Options</div>
        {!hasPosIntegration && (
          <div className="sm:ml-auto">
            <Button
              variant="default"
              className="flex items-center gap-1 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => {
                setOptionToEdit(null);
                setIsCreateOpen(true);
              }}
            >
              <Icon name="Plus" className="h-4 w-4" /> Add Option
            </Button>
          </div>
        )}
      </div>
      {options
        .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
        .map((option) => (
          <div
            key={option.id}
            className="border-border bg-background rounded-lg border"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 p-3">
              <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                <div className="text-foreground text-sm font-medium wrap-anywhere">
                  {option.name}
                </div>
                {(option.min != null || option.max != null) && (
                  <div className="text-muted-foreground text-xs">
                    Min {option.min ?? 0} · Max {option.max ?? 0}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!hasPosIntegration && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setOptionToEdit(option);
                        setIsCreateOpen(true);
                      }}
                      className="text-muted-foreground hover:bg-secondary hover:text-primary rounded-md p-1.5"
                      aria-label="Edit option"
                      title="Edit option"
                    >
                      <Icon name="Edit" className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleDeleteOption(option)}
                      disabled={deleteOptionMutation.isPending}
                      className="text-muted-foreground hover:bg-secondary hover:text-destructive rounded-md p-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Delete option"
                      title="Delete option"
                    >
                      <Icon name="Trash" className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Switch
                  checked={!!option.enabled}
                  onCheckedChange={(v) => handleToggleOption(option, v)}
                  disabled={toggleOptionMutation.isPending}
                />
              </div>
            </div>

            <div className="px-3 pb-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-muted-foreground text-xs">Suboptions</div>
                {!hasPosIntegration && (
                  <Button
                    variant="default"
                    onClick={() => handleCreateSuboption(option.id)}
                    className="flex items-center gap-1 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon name="Plus" className="h-3 w-3" /> Add
                  </Button>
                )}
              </div>
              {Array.isArray(option.suboptions) &&
              option.suboptions.length > 0 ? (
                <div className="space-y-2">
                  {option.suboptions
                    .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
                    .map((so) => (
                      <div
                        key={so.id}
                        className="border-border bg-secondary flex flex-wrap items-center justify-between gap-2 rounded-md border p-2"
                      >
                        <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                          <div className="text-foreground text-xs font-medium wrap-anywhere">
                            {so.name}
                          </div>
                          {so.description && (
                            <div className="text-muted-foreground text-[11px] wrap-anywhere">
                              {so.description}
                            </div>
                          )}
                        </div>
                        <span className="text-success mr-3 ml-2 shrink-0 text-xs font-semibold">
                          {parsePrice(so.price || 0)}
                        </span>
                        <div className="flex items-center gap-2">
                          {!hasPosIntegration && (
                            <>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  handleEditSuboption(option.id, so)
                                }
                                className="text-muted-foreground hover:bg-card hover:text-primary rounded-md p-1"
                                aria-label="Edit suboption"
                                title="Edit suboption"
                              >
                                <Icon name="Edit" className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  handleDeleteSuboption(option.id, so)
                                }
                                disabled={deleteSuboptionMutation.isPending}
                                className="text-muted-foreground hover:bg-card hover:text-destructive rounded-md p-1 disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label="Delete suboption"
                                title="Delete suboption"
                              >
                                <Icon name="Trash" className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          <Switch
                            checked={!!so.enabled}
                            onCheckedChange={(v) =>
                              handleToggleSuboption(option.id, so, v)
                            }
                            disabled={updatingSuboptionId === so.id}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-muted-foreground py-2 text-xs">
                  No suboptions available
                </div>
              )}
            </div>
          </div>
        ))}

      {!options.length && (
        <div className="text-muted-foreground p-3 text-sm">
          No options available
        </div>
      )}

      {pagination?.total_pages > 1 && (
        <div className="border-border mt-2 border-t pt-2">
          <Pagination
            currentPage={page}
            totalPages={pagination.total_pages}
            onPageChange={setPage}
            maxVisiblePages={5}
          />
        </div>
      )}

      {/* Create/Edit Option Modal */}
      <CreateOptionModal
        isOpen={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
          setOptionToEdit(null);
        }}
        businessId={businessId}
        extraId={extraId}
        optionToEdit={optionToEdit}
      />

      {/* Delete Option Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setOptionToDelete(null);
        }}
        onConfirm={confirmDeleteOption}
        itemName={optionToDelete?.name}
        itemType="Option"
        isLoading={deleteOptionMutation.isPending}
      />

      {/* Create/Edit Suboption Modal */}
      <CreateSuboptionModal
        isOpen={isCreateSuboptionOpen}
        onClose={() => {
          setIsCreateSuboptionOpen(false);
          setSuboptionToEdit(null);
          setCurrentOptionId(null);
        }}
        businessId={businessId}
        extraId={extraId}
        optionId={currentOptionId}
        suboptionToEdit={suboptionToEdit}
      />

      {/* Delete Suboption Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteSuboptionOpen}
        onClose={() => {
          setIsDeleteSuboptionOpen(false);
          setSuboptionToDelete(null);
          setCurrentOptionId(null);
        }}
        onConfirm={confirmDeleteSuboption}
        itemName={suboptionToDelete?.name}
        itemType="Suboption"
        isLoading={deleteSuboptionMutation.isPending}
      />
    </div>
  );
};

export default ExtraOptions;
